import CSS from "csstype";
import { useEffect, useRef, useState } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { Flipper, Flipped } from "react-flip-toolkit";
import { Cue } from "../../shared/interfaces/GameLogicTypes";
import useSocketContext from "../../shared/useSocketContext";
import { getSequenceBarFontSize, getSequenceBarDesktopFontSize } from "../../shared/helper/functions";
import { renderCueContent } from "../../shared/helper/renderCueContent";
import { track } from "../../shared/analytics";
import "./SequenceBar.css";

const BAR_BG = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const BAR_FG = ["#333333", "#ffffff", "#ffffff", "#ffffff", "#ffffff"];
const RANK_COLORS = ["#FFD700", "#B0B0B0", "#CD7F32", "#555555", "#c0392b"];
const RANK_STROKES = ["#8a7100", "#505050", "#7a4c1e", "#222222", "#7a1010"];

export interface SequenceBarProps {
  sequence: Array<number>;
  reset: () => void;
  style?: CSS.Properties;
  canSkip: boolean;
  decreaseRemainingSkips: () => void;
  remainingSkips: number;
  cue1: Cue;
  cue2: Cue;
  cue3: Cue;
  cue4: Cue;
  cue5: Cue;
  isMobile?: boolean;
  isShuffling?: boolean;
  /** If provided, overrides the default socket submit/unsubmit/forceSubmit behaviour */
  onSubmit?: (sequence: number[]) => void;
  forceSubmitEvent?: string;
  /** If true, hide the skip button regardless of canSkip */
  hideSkip?: boolean;
  /** If provided, overrides the default skipQuestion emit */
  onSkip?: () => void;
  /** Label override for the skip button */
  skipLabel?: React.ReactNode;
  /** If true, render display-only bars without drag-and-drop or action buttons */
  readOnly?: boolean;
  /** Mount in locked-in state (submission restored from server after a reconnect) */
  initialSubmitted?: boolean;
  /** Incremented by the parent when a standard-ranking drag begins */
  editStartVersion?: number;
}

export default function SequenceBar(props: SequenceBarProps) {
  const cues = [props.cue1, props.cue2, props.cue3, props.cue4, props.cue5];
  const getCueContent = (num: number): string =>
    String(cues[num - 1]?.content ?? "");

  // ── Read-only mode: just visual bars, no DnD or buttons ──
  if (props.readOnly) {
    return (
      <Paper sx={{ width: "100%", px: 1.5, pt: 1.5, pb: 1.5 }}>
        <Stack spacing={1}>
          {props.sequence.map((item, index) => (
            <Box
              key={item}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Typography
                style={{
                  minWidth: "1.75rem",
                  textAlign: "center",
                  color: RANK_COLORS[index],
                  fontSize: "1.5rem",
                  fontFamily: '"Bangers", cursive',
                  letterSpacing: "0.04em",
                  WebkitTextStroke: `0.0625rem ${RANK_STROKES[index]}`,
                  flexShrink: 0,
                  userSelect: "none",
                  lineHeight: 1,
                }}
              >
                {index + 1}
              </Typography>
              <Box
                style={{
                  flex: 1,
                  backgroundColor: BAR_BG[item - 1],
                  color: BAR_FG[item - 1],
                  borderRadius: "0.375rem",
                  padding: props.isMobile ? "0.8125rem 0.875rem" : "0.625rem 0.875rem",
                  height: props.isMobile ? "4.25rem" : undefined,
                  minHeight: props.isMobile ? undefined : "3.25rem",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    fontSize: props.isMobile ? getSequenceBarFontSize(getCueContent(item)) : getSequenceBarDesktopFontSize(getCueContent(item)),
                    fontWeight: 600,
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    color: "inherit",
                  }}
                >
                  {renderCueContent(getCueContent(item))}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  }

  // ── Interactive mode ──
  return <InteractiveSequenceBar {...props} cues={cues} getCueContent={getCueContent} />;
}

function InteractiveSequenceBar(
  props: SequenceBarProps & { cues: Cue[]; getCueContent: (num: number) => string }
) {
  const [submitted, setSubmitted] = useState(!!props.initialSubmitted);
  const sequenceRef = useRef(props.sequence);
  const submittedRef = useRef(submitted);
  const mountedRef = useRef(false);
  const editStartVersionRef = useRef(props.editStartVersion);
  const socket = useSocketContext().socket;

  const isValid = props.sequence.length === 5;

  // Keep refs current for the stale-closure-safe handlers below
  useEffect(() => {
    sequenceRef.current = props.sequence;
  }, [props.sequence]);
  useEffect(() => {
    submittedRef.current = submitted;
  }, [submitted]);

  // DragDropContext lives above this component. It withdraws the server-side
  // submission as soon as a drag starts, then increments this value so the
  // button immediately reflects the same unlocked state—even if the drag is
  // cancelled or dropped back in its original slot.
  useEffect(() => {
    if (
      props.editStartVersion === undefined ||
      editStartVersionRef.current === props.editStartVersion
    ) {
      return;
    }
    editStartVersionRef.current = props.editStartVersion;
    submittedRef.current = false;
    setSubmitted(false);
  }, [props.editStartVersion]);

  // Fallback for a locked sequence change that did not come through the parent
  // drag-start signal. Skipped on mount so a reconnect/remount never withdraws
  // the submission that was just restored from the server.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (submittedRef.current) {
      setSubmitted(false);
      if (!props.onSubmit) {
        socket.emit("unsubmit");
      }
    }
  }, [props.sequence]);

  // Force-submit when the round timer expires
  useEffect(() => {
    const forceEvent = props.forceSubmitEvent ?? "forceSubmit";
    const handler = () => {
      if (props.onSubmit) {
        props.onSubmit(sequenceRef.current);
      } else {
        socket.emit("submit", sequenceRef.current);
      }
    };
    socket.on(forceEvent, handler);
    return () => {
      socket.off(forceEvent, handler);
    };
  }, [props.forceSubmitEvent, props.onSubmit]);


  return (
    <Paper sx={{ width: "100%", px: 1.5, pt: 1.5, pb: 1.5 }}>
      {/* ── Vertical drag list with FLIP animation ─────────── */}
      <Flipper flipKey={props.sequence.join("")} spring={{ stiffness: 220, damping: 22 }}>
        <Droppable droppableId="sequenceBar" direction="vertical">
          {(provided) => (
            <Stack
              ref={provided.innerRef}
              {...provided.droppableProps}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              {props.sequence.map((item, index) => (
                <Draggable
                  key={`${item}-draggable`}
                  draggableId={`${item}-draggable`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Flipped
                      flipId={`item-${item}`}
                      translate={true}
                      shouldFlip={() => !snapshot.isDragging}
                    >
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          opacity: snapshot.isDragging ? 0.85 : 1,
                        }}
                      >
                        {/* Rank number */}
                        <Typography
                          style={{
                            minWidth: "1.75rem",
                            textAlign: "center",
                            color: RANK_COLORS[index],
                            fontSize: "1.5rem",
                            fontFamily: '"Bangers", cursive',
                            letterSpacing: "0.04em",
                            WebkitTextStroke: `0.0625rem ${RANK_STROKES[index]}`,
                            flexShrink: 0,
                            userSelect: "none",
                            lineHeight: 1,
                          }}
                        >
                          {index + 1}
                        </Typography>

                        {/* Coloured content bar */}
                        <Box
                          style={{
                            flex: 1,
                            backgroundColor: BAR_BG[item - 1],
                            color: BAR_FG[item - 1],
                            borderRadius: "0.375rem",
                            padding: props.isMobile ? "0.8125rem 0.875rem" : "0.625rem 0.875rem",
                            height: props.isMobile ? "4.25rem" : undefined,
                            minHeight: props.isMobile ? undefined : "3.25rem",
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: props.isMobile ? getSequenceBarFontSize(props.getCueContent(item)) : getSequenceBarDesktopFontSize(props.getCueContent(item)),
                              fontWeight: 600,
                              lineHeight: 1.3,
                              wordBreak: "break-word",
                              color: "inherit",
                            }}
                          >
                            {renderCueContent(props.getCueContent(item))}
                          </Typography>
                        </Box>

                        {/* Drag handle indicator */}
                        <DragIndicatorIcon
                          sx={{ color: "text.disabled", flexShrink: 0 }}
                        />
                      </Box>
                    </Flipped>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </Flipper>

      {/* ── Action buttons ───────────────────────────────────── */}
      <Stack direction="row" spacing={1}>
        {props.canSkip && !props.hideSkip && (
          <Button
            variant="contained"
            color="secondary"
            // A vote-skip (skipLabel set) is off the table once you've locked in — the answer is
            // already cast, same rule as Reverse Round.
            disabled={props.remainingSkips === 0 || (!!props.skipLabel && submitted)}
            onClick={() => {
              if (props.onSkip) {
                props.onSkip();
              } else {
                track("question_skipped");
                socket.emit("skipQuestion");
                props.decreaseRemainingSkips();
              }
            }}
            sx={{ flex: 1, borderRadius: 2, py: 1.5 }}
          >
            {props.skipLabel ?? (
              <Trans values={{ remainingSkips: props.remainingSkips }}>
                {t("skip")}
              </Trans>
            )}
          </Button>
        )}
        <Button
          variant={submitted ? "outlined" : "contained"}
          disabled={!isValid || !!props.isShuffling}
          onClick={() => {
            // Emit only on explicit user intent — never from mounts or programmatic resets
            const next = !submitted;
            setSubmitted(next);
            if (next) {
              track("turn_submit");
              if (props.onSubmit) {
                props.onSubmit(props.sequence);
              } else {
                socket.emit("submit", props.sequence);
              }
            } else if (!props.onSubmit) {
              socket.emit("unsubmit");
            }
          }}
          sx={{
            flex: 4,
            borderRadius: 2,
            fontWeight: "bold",
            fontSize: { xs: "1rem", md: "1.1rem" },
            py: 1.5,
            boxSizing: "border-box",
            height: "3rem",
          }}
        >
          {submitted ? t("waiting") : t("lock_in")}
        </Button>
      </Stack>
    </Paper>
  );
}
